// src/hooks/usePricingTrips.js
import { useState, useCallback } from "react";
import { db } from "../firebase/firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
} from "firebase/firestore";

// ✅ IMPORT KOI PRICING ENGINE
import { priceKOI } from "../utils/tripPricingEngine";

function applyKOIValidation(r) {
  const isKOI =
    (r.clientId || r.clientKey || "").toString().toLowerCase().includes("koi");

  if (!isKOI) return r;

  // determine expected
  const expected = priceKOI({
  pickup: r.pickup,
  drop: r.drop,
  pickupLat: r.pickupLat,
  pickupLng: r.pickupLng,
  dropLat: r.dropLat,
  dropLng: r.dropLng,
  carType: r.carType,
  cfg: {
    rules: {
      koiRates: {
        airportWithinBoundary: { standard: 78, executive: 98 },
        airportBeyondBoundary: { standard: 93, executive: 113 },
        abuDhabi: { standard: 248, executive: 268 },
      },
      distanceThresholdKm: 20
    },
    // ⭐ pass comment text
    currentRowComment: r.comment
  },
});



  const actual = Number(r.clientFinalPrice) || 0;

  r.koiExpectedPrice = expected;
  r.koiActualPrice = actual;
  r.priceMismatch = expected !== actual;
  r.priceDiff = actual - expected;

  if (!expected || expected === 0) {
    r.priceStatus = "⚠ UNKNOWN";
  } else if (expected !== actual) {
    r.priceStatus = "❌ WRONG PRICE";
  } else {
    r.priceStatus = "✔ OK";
  }

  return r;
}

export function usePricingTrips({ clientId, batchDate, pageSize = 100 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState("pending");

  const loadTrips = useCallback(
    async (reset = true) => {
      if (!clientId || !batchDate) {
        alert("Select client and date first.");
        return;
      }

      setLoading(true);

      try {
        // ⭐ CASE 1: ALL CLIENTS
        if (clientId === "all_clients") {
          const tripImportsSnap = await getDocs(collection(db, "tripImports"));
          let combined = [];

          for (const imp of tripImportsSnap.docs) {
            const id = imp.id;

            // Must match selected date
            if (!id.endsWith(batchDate)) continue;

            const tripsRef = collection(db, `tripImports/${id}/trips`);

            let qRef;
            if (filter === "pending") {
              qRef = query(tripsRef, where("pricingDone", "==", false));
            } else if (filter === "done") {
              qRef = query(tripsRef, where("pricingDone", "==", true));
            } else {
              qRef = query(tripsRef, orderBy("createdAt", "asc"));
            }

            const tripsSnap = await getDocs(qRef);

            const data = tripsSnap.docs.map((d) =>
              applyKOIValidation({
                id: d.id,
                ...d.data(),
                clientKey: id.split("_")[0],
                realBatchKey: id,
              })
            );

            combined.push(...data);
          }

          // sort by creation time
          combined.sort(
            (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
          );

          setRows(combined);
          setHasMore(false);
          setLastDoc(null);
          setLoading(false);
          return;
        }

        // ⭐ CASE 2: SINGLE CLIENT
        const batchKey = `${clientId}_${batchDate}`;
        const baseRef = collection(db, `tripImports/${batchKey}/trips`);

        let qRef;

        if (filter === "pending") {
          qRef = query(
            baseRef,
            where("pricingDone", "==", false),
            limit(pageSize)
          );
        } else if (filter === "done") {
          qRef = query(
            baseRef,
            where("pricingDone", "==", true),
            limit(pageSize)
          );
        } else {
          qRef = query(baseRef, orderBy("createdAt", "asc"), limit(pageSize));
        }

        if (!reset && lastDoc && filter === "all") {
          qRef = query(
            baseRef,
            orderBy("createdAt", "asc"),
            startAfter(lastDoc),
            limit(pageSize)
          );
        }

        const snap = await getDocs(qRef);

        const data = snap.docs.map((d) =>
          applyKOIValidation({
            id: d.id,
            ...d.data(),
            clientKey: clientId,
            realBatchKey: batchKey,
          })
        );

        setRows((prev) => (reset ? data : [...prev, ...data]));
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.docs.length === pageSize);
      } catch (err) {
        console.error("Error loading pricing trips:", err);
        alert("Failed to load trips.");
      } finally {
        setLoading(false);
      }
    },
    [clientId, batchDate, filter, lastDoc, pageSize]
  );

  return {
    rows,
    setRows,
    loading,
    filter,
    setFilter,
    hasMore,
    loadTrips,
  };
}
